/**
 * Photo annotation storage format, version 2.
 *
 * ## Why v2 exists
 *
 * v1 stored annotation geometry as raw device pixels measured against a
 * `screenWidth - 32` wide, 4:3 box, with the photo drawn `resizeMode="cover"`.
 * Two consequences: the numbers depended on which phone drew them, and they
 * referred to a *crop* of the photo rather than the photo. The same annotation
 * already landed in the wrong place on a different device width, and zoom,
 * landscape and server-side burn-in were all impossible.
 *
 * v2 is image-relative: every coordinate is a fraction of the photo itself, so
 * one payload renders identically on a 375pt phone, a rotated tablet, and a
 * 4000px JPEG on the server.
 *
 * ## The render contract
 *
 * This block is the specification. `annotationGeometry.ts` implements it for
 * both browser SVG and react-native-svg. The Pillow notes below describe how
 * a future burn-in renderer should match this contract; email/download copies
 * currently use the original image.
 *
 * Given a target raster `W x H` px — for Pillow the JPEG, for RN the
 * contain-fitted display rect — let `D = hypot(W, H)` and
 * `px(p) = [p[0] * W, p[1] * H]`.
 *
 *   strokePx = max(MIN_STROKE_PX, round(item.width * D))
 *   fontPx   = max(MIN_FONT_PX,   round(item.size  * D))
 *
 * - **arrow** — a line from `from` to `to` with round caps, plus two head
 *   segments drawn *from* `to`, each `headLen` long, at `±ARROW_WING_ANGLE`
 *   off the reversed shaft direction. Three separate segments — an open V,
 *   not a filled triangle. `headLen` comes from `arrowHeadLength()`, which
 *   clamps against the shaft length so a short arrow does not get a head
 *   longer than itself (a v1 artifact).
 * - **ellipse** — axis-aligned, centred on `center` with radii `rx * W` and
 *   `ry * H`. Note Pillow strokes an ellipse *inward* from its bounding box
 *   while SVG strokes centred on the path, a `strokePx / 2` difference that is
 *   sub-perceptual at 1600px and deliberately not corrected.
 * - **freehand** — a polyline through `points` with round caps and joins.
 *   Pillow has no round cap, so it additionally fills a dot of radius
 *   `strokePx / 2` at the first and last point.
 * - **text** — a single line anchored at the **left end of its baseline**.
 *   That is SVG's default and Pillow's `anchor="ls"`; it is the only anchor
 *   the two agree on exactly, and it is what v1 used, so text converts with no
 *   vertical correction. When `halo` is set, a black outline of
 *   `max(TEXT_HALO_MIN_PX, round(TEXT_HALO_RATIO * fontPx))` is drawn behind
 *   the glyphs so labels stay legible over a pale roof.
 * - **order** — `items[0]` is painted first, so the last item is on top.
 *
 * ## Why the fields are shaped this way
 *
 * - The document is an object, not a bare array, so it still satisfies the
 *   backend's `JSONField(default=dict)`. `items` is an array because paint
 *   order matters and a keyed record has no defined order — v1 relied on
 *   `Object.values()` insertion order, which is an implementation detail.
 * - Points are `[x, y]` tuples rather than `{x, y}`. A freehand stroke carries
 *   hundreds of them, and the tuple form is roughly 40% of the bytes while
 *   staying unambiguous in Python (`x, y = point`).
 * - `x` is normalized by image width and `y` by height, independently. Because
 *   both renderers scale each axis by its own dimension, an axis-aligned
 *   ellipse stays axis-aligned. There is no rotation in the schema, so there
 *   is no case where independent scaling would shear a shape.
 * - `width` and `size` are fractions of the image **diagonal**. Under a
 *   `contain` fit the display scale `s = min(boxW/imgW, boxH/imgH)` is uniform,
 *   so `frac * displayDiagonal` is identically `s * (frac * imageDiagonal)` —
 *   RN and Pillow agree by construction rather than by calibration. Diagonal
 *   rather than width so a panorama does not get an absurdly fat stroke.
 * - Ellipses are centre + radii rather than a two-point bounding box, which
 *   maps straight onto `<Ellipse cx cy rx ry>` and `draw.ellipse(bbox, width=)`
 *   with none of v1's two-arc SVG path trickery.
 */

/** A point in image space. `[x / imageWidth, y / imageHeight]`, both in [0, 1]. */
export type NPoint = [number, number];

export const SCHEMA_VERSION = 2 as const;

interface ItemBase {
  id: string;
  /** '#RRGGBB', uppercase. Normalized on read; never a named or rgba() colour. */
  color: string;
  createdAt: string;
  updatedAt: string;
}

interface StrokedBase extends ItemBase {
  /** Stroke width as a fraction of the image diagonal. */
  width: number;
}

export interface ArrowItem extends StrokedBase {
  kind: 'arrow';
  /** Tail — where the drag started. */
  from: NPoint;
  /** Head — where the arrowhead is drawn. */
  to: NPoint;
}

export interface EllipseItem extends StrokedBase {
  kind: 'ellipse';
  center: NPoint;
  /** Horizontal radius as a fraction of image width. */
  rx: number;
  /** Vertical radius as a fraction of image height. */
  ry: number;
}

export interface FreehandItem extends StrokedBase {
  kind: 'freehand';
  /** At least one point; simplified and capped at MAX_FREEHAND_POINTS. */
  points: NPoint[];
}

export interface TextItem extends ItemBase {
  kind: 'text';
  /** Left end of the baseline of the single line of text. */
  at: NPoint;
  /** Single line — newlines are stripped, because line-height rules differ between renderers. */
  text: string;
  /** Em size as a fraction of the image diagonal. */
  size: number;
  /** Draw a dark outline behind the glyphs. */
  halo: boolean;
}

export type AnnotationItem = ArrowItem | EllipseItem | FreehandItem | TextItem;

export interface AnnotationDoc {
  schema: typeof SCHEMA_VERSION;
  /** Paint order: index 0 is painted first and sits at the bottom. */
  items: AnnotationItem[];
}

export const EMPTY_DOC: AnnotationDoc = { schema: SCHEMA_VERSION, items: [] };

// --- Render contract constants (shared with the Python renderer) ---

/** Floor so a hairline stroke on a small preview stays visible. */
export const MIN_STROKE_PX = 1.5;
/** Floor so a label on a thumbnail stays legible rather than becoming a smudge. */
export const MIN_FONT_PX = 9;

/** Arrowhead wings sit 30 degrees off the shaft, matching v1's `Math.PI / 6`. */
export const ARROW_WING_ANGLE = Math.PI / 6;
/** Preferred head length, as a multiple of stroke width. */
export const ARROW_HEAD_RATIO = 4.5;
/** Never shrink the head below this multiple of stroke width — it stops reading as an arrow. */
export const ARROW_HEAD_MIN_RATIO = 2;
/** Never let the head eat more than this fraction of the shaft. v1 had no such clamp. */
export const ARROW_HEAD_MAX_SHAFT_FRACTION = 0.4;

export const TEXT_HALO_RATIO = 0.1;
export const TEXT_HALO_MIN_PX = 2;
/**
 * Mean glyph advance as a fraction of the em size, used only to give text a
 * hit box and a selection outline. Deliberately approximate: react-native-svg
 * has no synchronous text measurement, and the true advance depends on a font
 * the server does not share with the device.
 */
export const TEXT_ADVANCE_RATIO = 0.55;
/** Baseline-relative extent of the hit box, in ems. */
export const TEXT_ASCENT_RATIO = 0.8;
export const TEXT_DESCENT_RATIO = 0.2;

// --- Authoring defaults ---

/**
 * v1's hardcoded style was `strokeWidth: 4` / `fontSize: 18` in a box that was
 * 361 x 271 on a 393pt iPhone — a diagonal of ~451pt. These are those values
 * expressed in v2 units, so a new annotation looks like the ones reps are used to.
 */
export const DEFAULT_WIDTH = 0.009;
export const DEFAULT_SIZE = 0.04;
export const DEFAULT_COLOR = '#FF3B30';

/** Fallback for a colour v1 stored that would render invisible or unparseable. */
export const FALLBACK_COLOR = '#FF0000';

export const PALETTE = [
  '#FF3B30',
  '#FFCC00',
  '#34C759',
  '#00A3FF',
  '#FFFFFF',
  '#111111',
] as const;

/** thin / medium / thick */
export const WIDTH_STEPS = [0.005, 0.009, 0.016] as const;
export const SIZE_STEPS = [0.028, 0.04, 0.058, 0.08] as const;

/**
 * A fast drag emits a point per frame. Simplify to this tolerance (in
 * normalized units) and hard-cap the count, so one stroke cannot bloat the
 * photo's JSONField without bound.
 */
export const RDP_EPSILON = 0.002;
export const MAX_FREEHAND_POINTS = 400;

/** Longest label we will store. Guards the payload, not the UI. */
export const MAX_TEXT_LENGTH = 200;

// --- Small shared helpers ---

/**
 * 4 decimal places is 0.4px on a 4000px image — below perception, and it
 * roughly halves the stored payload. `+ 0` normalizes -0 to 0 so two
 * fingerprints of the same document always compare equal.
 */
export function round4(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10000) / 10000 + 0;
}

export function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

const HEX6 = /^#([0-9a-f]{6})$/i;
const HEX3 = /^#([0-9a-f]{3})$/i;

/**
 * Coerce a colour into an uppercase 6-digit hex.
 *
 * Anything the Python renderer could not parse — `rgba(...)`, a CSS colour
 * name, a non-string — becomes the fallback rather than reaching Pillow, which
 * would raise on it mid-send.
 *
 * Note this deliberately does *not* touch white or black: both are in the
 * authoring palette and a rep who picks white for a label over a dark shingle
 * means it. See `normalizeLegacyColor` for the v1 rule.
 */
export function normalizeColor(raw: unknown): string {
  if (typeof raw !== 'string') return FALLBACK_COLOR;
  const value = raw.trim();
  const short = HEX3.exec(value);
  if (short) {
    const [r, g, b] = short[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  const long = HEX6.exec(value);
  if (long) return `#${long[1]}`.toUpperCase();
  return FALLBACK_COLOR;
}

/**
 * The v1 colour rule, for migration only.
 *
 * The old canvas had no colour picker: it hardcoded red and then, at draw
 * time, rewrote black, white and transparent to red in two separate places
 * (`AnnotationCanvas.tsx`). So a v1 annotation stored as `#000000` was
 * *displayed* red, and migrating it faithfully means writing red — not black,
 * which would change how an existing annotation looks.
 *
 * This must not be applied to v2 documents, where those colours are choices.
 */
export function normalizeLegacyColor(raw: unknown): string {
  if (typeof raw !== 'string') return FALLBACK_COLOR;
  const value = raw.trim().toLowerCase();
  if (
    value === '' ||
    value === 'transparent' ||
    value === '#000000' ||
    value === '#fff' ||
    value === '#ffffff'
  ) {
    return FALLBACK_COLOR;
  }
  return normalizeColor(raw);
}

/** Single line only — see the TextItem docs. */
export function normalizeText(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  return raw.replace(/[\r\n]+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH);
}

let idCounter = 0;

/** Ids only need to be unique within one photo's document. */
export function makeAnnotationId(kind: AnnotationItem['kind']): string {
  idCounter += 1;
  return `${kind[0]}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}
